import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { fetchProjects, fetchProjectTables, removeProjectTables } from 'store/slices/project';
import TableMongoDB from '../workspace/storage/mongodb/TableMongoDB';
import CollectionsBrowser from './components/CollectionsBrowser';
import QueryBuilder from './components/QueryBuilder';
import DatabaseAnalytics from './components/DatabaseAnalytics';
import ConnectionsManager from './components/ConnectionsManager';

// icons
import {
    IconDatabase,
    IconRefresh,
    IconSearch,
    IconPlus,
    IconSettings,
    IconTable,
    IconServer,
    IconCloud,
    IconCopy,
    IconEye,
    IconFolder,
    IconCode,
    IconAnalyze,
    IconLink
} from '@tabler/icons-react';

// assets
import MongoDBLogo from '../../assets/icons/mongodb_logo.png';

// ==============================|| DATABASE DASHBOARD ||============================== //

const DatabaseDashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const { projects, tables } = useSelector((state) => state.project);

    const [loading, setLoading] = useState(false);
    const [showTableManager, setShowTableManager] = useState(false);
    const [showConnectionDialog, setShowConnectionDialog] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filterType, setFilterType] = useState('all');
    const [tabValue, setTabValue] = useState(0);
    const [connectionInfo, setConnectionInfo] = useState({
        uri: '',
        database: '',
        username: '',
        password: ''
    });
    const [databaseMetrics, setDatabaseMetrics] = useState({
        totalCollections: 0,
        totalDocuments: 0,
        totalProjects: 0,
        activeConnections: 0
    });

    // Map routes to tab indices
    const routeToTab = {
        '/database/dashboard': 0,
        '/database/collections': 1,
        '/database/query': 2,
        '/database/analytics': 3,
        '/database/connections': 4
    };

    useEffect(() => {
        // Set tab based on current route
        const currentTab = routeToTab[location.pathname] || 0;
        setTabValue(currentTab);

        loadProjects();
        loadDatabaseMetrics();
    }, [location.pathname]);

    const loadProjects = async () => {
        setLoading(true);
        try {
            await dispatch(fetchProjects());
        } catch (error) {
            console.error('Error loading projects:', error);
        }
        setLoading(false);
    };

    const loadDatabaseMetrics = async () => {
        // Mock data - replace with actual API call
        setDatabaseMetrics({
            totalCollections: 156,
            totalDocuments: 48257,
            totalProjects: projects?.length || 0,
            activeConnections: 12
        });
    };

    const handleProjectSelect = (project) => {
        setSelectedProject(project);
        setShowTableManager(true);
        dispatch(fetchProjectTables(project.project_name));
    };

    const handleBackToProjects = () => {
        setShowTableManager(false);
        setSelectedProject(null);
        dispatch(removeProjectTables());
    };

    const handleShowConnection = (project) => {
        setSelectedProject(project);
        setConnectionInfo({
            uri: project.mongodb_uri || `mongodb://localhost:27017/db_${project.project_name}_${project.user_token}`,
            database: `db_${project.project_name}_${project.user_token}`,
            username: project.mongodb_username || 'admin',
            password: project.mongodb_passwd || '••••••••'
        });
        setShowConnectionDialog(true);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Show toast notification
    };

    const filteredProjects =
        projects?.filter((project) => {
            const matchesSearch =
                project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.description?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        }) || [];

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.project_name.localeCompare(b.project_name);
            case 'tables':
                return (b.project_table_num || 0) - (a.project_table_num || 0);
            case 'date':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                return 0;
        }
    });

    // Status cards data
    const statusCards = [
        {
            title: 'Total Collections',
            value: databaseMetrics.totalCollections.toLocaleString(),
            icon: <IconTable size={24} />,
            color: theme.palette.primary.main,
            description: 'Across all projects'
        },
        {
            title: 'Total Documents',
            value: databaseMetrics.totalDocuments.toLocaleString(),
            icon: <IconDatabase size={24} />,
            color: theme.palette.secondary.main,
            description: 'MongoDB documents'
        },
        {
            title: 'Active Projects',
            value: databaseMetrics.totalProjects,
            icon: <IconServer size={24} />,
            color: theme.palette.success.main,
            description: 'Projects with databases'
        },
        {
            title: 'Active Connections',
            value: databaseMetrics.activeConnections,
            icon: <IconCloud size={24} />,
            color: theme.palette.warning.main,
            description: 'Current connections'
        }
    ];

    if (showTableManager && selectedProject) {
        return (
            <MainCard
                title={
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconDatabase />
                        <Typography variant="h3">Database Manager - {selectedProject.project_name}</Typography>
                        <Chip label={`${selectedProject.project_table_num || 0} collections`} size="small" color="secondary" />
                    </Box>
                }
                secondary={
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={handleBackToProjects} size="small">
                            Back to Projects
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<IconPlus />}
                            onClick={() => handleShowConnection(selectedProject)}
                            size="small"
                        >
                            Connection Info
                        </Button>
                    </Box>
                }
            >
                <TableMongoDB
                    onClick={handleBackToProjects}
                    folderName={selectedProject.project_name}
                    projectSubname={selectedProject.project_name}
                    dataMongoDBTable={tables}
                    fetchProjectTables={() => dispatch(fetchProjectTables(selectedProject.project_name))}
                    isSetting={false}
                />
            </MainCard>
        );
    }

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={2}>
                    <IconDatabase />
                    <Typography variant="h3">Database Management</Typography>
                    <Chip
                        label="MongoDB"
                        size="small"
                        sx={{
                            bgcolor: theme.palette.secondary.light,
                            color: theme.palette.secondary.dark
                        }}
                    />
                </Box>
            }
            secondary={
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadProjects} disabled={loading}>
                            <IconRefresh />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<IconSettings />}
                        onClick={() => setShowConnectionDialog(true)}
                        disabled={!selectedProject}
                    >
                        Database Settings
                    </Button>
                </Box>
            }
        >
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Status Cards */}
            <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                {statusCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    transition: 'transform 0.3s ease-in-out'
                                }
                            }}
                        >
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            {card.title}
                                        </Typography>
                                        <Typography variant="h3" component="div">
                                            {card.value}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {card.description}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            backgroundColor: `${card.color}20`,
                                            borderRadius: '12px',
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {React.cloneElement(card.icon, { style: { color: card.color } })}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconFolder size={18} />
                                Projects
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconTable size={18} />
                                Collections
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconCode size={18} />
                                Query Builder
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconAnalyze size={18} />
                                Analytics
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <IconLink size={18} />
                                Connections
                            </Box>
                        }
                    />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {tabValue === 0 && (
                <>
                    {/* Filter Section */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search projects..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ flexGrow: 1, maxWidth: 400 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Sort by</InputLabel>
                            <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="tables">Collections</MenuItem>
                                <MenuItem value="date">Created Date</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Filter</InputLabel>
                            <Select value={filterType} label="Filter" onChange={(e) => setFilterType(e.target.value)}>
                                <MenuItem value="all">All Projects</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Projects Grid */}
                    <Grid container spacing={gridSpacing}>
                        {sortedProjects.map((project, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8],
                                            transition: 'all 0.3s ease-in-out'
                                        },
                                        border: '1px solid',
                                        borderColor: theme.palette.divider
                                    }}
                                >
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                                            <Box
                                                sx={{
                                                    backgroundColor: '#673AB720',
                                                    borderRadius: '12px',
                                                    p: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <img src={MongoDBLogo} alt="MongoDB" style={{ width: 32, height: 32 }} />
                                            </Box>
                                            <Box flexGrow={1}>
                                                <Typography variant="h5" gutterBottom>
                                                    {project.project_name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {project.description || 'No description'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <Box>
                                                <Typography variant="h6" color="secondary">
                                                    {project.project_table_num || 0}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Collections
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="textSecondary">
                                                    Created: {new Date(project.created_at).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box display="flex" gap={1}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<IconDatabase />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleProjectSelect(project);
                                                }}
                                                fullWidth
                                            >
                                                Manage
                                            </Button>
                                            <Button
                                                variant="text"
                                                size="small"
                                                startIcon={<IconEye />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleShowConnection(project);
                                                }}
                                            >
                                                Connection
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {sortedProjects.length === 0 && (
                        <Box textAlign="center" py={8}>
                            <IconDatabase size={64} style={{ opacity: 0.3, marginBottom: 16 }} />
                            <Typography variant="h4" gutterBottom sx={{ opacity: 0.5 }}>
                                No database projects found
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                                {searchTerm ? 'Try adjusting your search terms' : 'Create your first project to get started'}
                            </Typography>
                        </Box>
                    )}

                    {/* Connection Info Dialog */}
                    <Dialog open={showConnectionDialog} onClose={() => setShowConnectionDialog(false)} maxWidth="md" fullWidth>
                        <DialogTitle>
                            <Box display="flex" alignItems="center" gap={2}>
                                <IconDatabase />
                                MongoDB Connection Info
                                {selectedProject && <Chip label={selectedProject.project_name} size="small" color="secondary" />}
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Use these credentials to connect to your MongoDB database from external applications.
                                    </Alert>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Connection URI"
                                        value={connectionInfo.uri}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => copyToClipboard(connectionInfo.uri)}>
                                                        <IconCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Database Name"
                                        value={connectionInfo.database}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => copyToClipboard(connectionInfo.database)}>
                                                        <IconCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Username"
                                        value={connectionInfo.username}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => copyToClipboard(connectionInfo.username)}>
                                                        <IconCopy />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        <Typography variant="body2">
                                            <strong>Security Note:</strong> Keep your database credentials secure and never share them
                                            publicly.
                                        </Typography>
                                    </Alert>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowConnectionDialog(false)}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}

            {/* Collections Browser Tab */}
            {tabValue === 1 && <CollectionsBrowser />}

            {/* Query Builder Tab */}
            {tabValue === 2 && <QueryBuilder />}

            {/* Analytics Tab */}
            {tabValue === 3 && <DatabaseAnalytics />}

            {/* Connections Tab */}
            {tabValue === 4 && <ConnectionsManager />}
        </MainCard>
    );
};

export default DatabaseDashboard;
